import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  $getRoot,
  $getNodeByKey,
  $isTextNode,
  EditorState,
  LexicalEditor,
  REDO_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { IInlineBox } from '$/components/InlineBox/types';
import type { MarkResult } from '$/components/InlineBox/types';
import { serializeEditorStateToContent } from './editor';
import { insertMentionNode, MentionMenuState } from './plugins';
import { $isMentionNode } from './nodes';
import { collectNodes } from './pluginUtils';
import {
  AtImageOption,
  EditorSerializedContent,
  InlineInsertAnchor,
  MarkInsertOptions,
  MentionOption,
  SkillOption,
} from './types';

export const useLatestRef = <T>(value: T) => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

export const useEditorUiState = () => {
  const [isFocused, setIsFocused] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const isComposingRef = useRef(false);
  isComposingRef.current = isComposing;

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback((onBlur?: () => void) => {
    setIsFocused(false);
    onBlur?.();
  }, []);

  const handleCompositionStart = useCallback((event: React.CompositionEvent<HTMLDivElement>) => {
    const target = event.target as any;
    target.composing = true;
    setIsComposing(true);
  }, []);

  const handleCompositionEnd = useCallback((event: React.CompositionEvent<HTMLDivElement>) => {
    const target = event.target as any;
    target.composing = false;
    setIsComposing(false);
  }, []);

  return {
    isFocused,
    isComposing,
    isComposingRef,
    handleBlur,
    handleCompositionEnd,
    handleCompositionStart,
    handleFocus,
  };
};

export const useMentionMenuController = ({
  mentionOptions,
  editorRef,
  onKeyDown,
  onMentionSelect,
  isComposing,
  maxMentions,
  onMaxMentionsReached,
}: {
  mentionOptions: MentionOption[];
  editorRef: React.MutableRefObject<LexicalEditor | null>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onMentionSelect?: (option: MentionOption) => void;
  isComposing: boolean;
  maxMentions?: number;
  onMaxMentionsReached?: () => void;
}) => {
  const [mentionMenuState, setMentionMenuState] = useState<MentionMenuState | null>(null);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);

  const closeMentionMenu = useCallback(() => {
    setMentionMenuState(null);
  }, []);

  const filteredMentionOptions = useMemo(() => {
    if (!mentionMenuState) return [];
    const query = mentionMenuState.query.trim().toLowerCase();
    return mentionOptions.filter((option) => {
      if (!query) return true;
      const keywords = option.keywords || [];
      return option.name.toLowerCase().includes(query)
        || keywords.some((keyword) => keyword.toLowerCase().includes(query));
    });
  }, [mentionMenuState, mentionOptions]);

  useEffect(() => {
    setActiveMentionIndex(0);
  }, [mentionMenuState?.query]);

  useEffect(() => {
    if (activeMentionIndex < filteredMentionOptions.length) return;
    setActiveMentionIndex(0);
  }, [activeMentionIndex, filteredMentionOptions.length]);

  useEffect(() => {
    if (!filteredMentionOptions.length && mentionMenuState) {
      closeMentionMenu();
    }
  }, [closeMentionMenu, filteredMentionOptions.length, mentionMenuState]);

  const onMentionSelectRef = useLatestRef(onMentionSelect);
  const onMaxMentionsReachedRef = useLatestRef(onMaxMentionsReached);

  const insertMention = useCallback((option: MentionOption) => {
    if (!mentionMenuState) return;

    const editor = editorRef.current;
    if (!editor) return;

    if (maxMentions != null) {
      let currentCount = 0;
      editor.getEditorState().read(() => {
        const nodes: import('lexical').LexicalNode[] = [];
        $getRoot().getChildren().forEach((child) => collectNodes(child, nodes));
        currentCount = nodes.filter((n) => $isMentionNode(n)).length;
      });
      if (currentCount >= maxMentions) {
        closeMentionMenu();
        onMaxMentionsReachedRef.current?.();
        return;
      }
    }

    const triggerState = mentionMenuState;
    closeMentionMenu();

    insertMentionNode({ editor, option, triggerState });
    onMentionSelectRef.current?.(option);
  }, [closeMentionMenu, editorRef, maxMentions, mentionMenuState, onMentionSelectRef, onMaxMentionsReachedRef]);

  const handleEnterCommand = useCallback(() => {
    if (isComposing) return false;
    if (!mentionMenuState || !filteredMentionOptions.length) return false;

    insertMention(filteredMentionOptions[activeMentionIndex]);
    return true;
  }, [
    activeMentionIndex,
    filteredMentionOptions,
    insertMention,
    isComposing,
    mentionMenuState,
  ]);

  /** 处理联想菜单与撤销快捷键，并忽略缺少 key 的兼容键盘事件。 */
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isComposing) return;

    const editor = editorRef.current;
    const lowerCaseKey = typeof event.key === 'string' ? event.key.toLowerCase() : '';
    const isUndoShortcut = (event.metaKey || event.ctrlKey)
      && !event.shiftKey
      && lowerCaseKey === 'z';
    const isRedoShortcut = (event.metaKey && event.shiftKey && lowerCaseKey === 'z')
      || (event.ctrlKey && lowerCaseKey === 'y');

    if (editor && (isUndoShortcut || isRedoShortcut)) {
      event.preventDefault();
      event.stopPropagation();
      editor.dispatchCommand(isUndoShortcut ? UNDO_COMMAND : REDO_COMMAND, undefined);
      return;
    }

    if (mentionMenuState && filteredMentionOptions.length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveMentionIndex((prev) => ((prev + 1) % filteredMentionOptions.length));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveMentionIndex((prev) => ((prev - 1 + filteredMentionOptions.length) % filteredMentionOptions.length));
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMentionMenu();
        return;
      }
    }

    onKeyDown?.(event);
  }, [
    closeMentionMenu,
    editorRef,
    filteredMentionOptions,
    isComposing,
    mentionMenuState,
    onKeyDown,
  ]);

  return {
    activeMentionIndex,
    closeMentionMenu,
    filteredMentionOptions,
    handleKeyDown,
    handleEnterCommand,
    insertMention,
    mentionMenuState,
    setMentionMenuState,
  };
};

const filterSuggestionOptions = <T extends { name: string; keywords?: string[]; }>(
  options: T[],
  query: string,
) => {
  const normalizedQuery = query.trim().toLowerCase();
  return options.filter((option) => {
    if (!normalizedQuery) return true;
    const keywords = option.keywords || [];
    return option.name.toLowerCase().includes(normalizedQuery)
      || keywords.some((keyword) => keyword.toLowerCase().includes(normalizedQuery));
  });
};

/** 删除 @ 或 / 触发文本并把光标留在原位置，避免空输入框整段删除后丢失插入锚点。 */
const replaceTriggerText = ({
  editor,
  triggerState,
}: {
  editor: LexicalEditor;
  triggerState: MentionMenuState;
}) => {
  const insertAnchor: InlineInsertAnchor = {
    nodeKey: triggerState.nodeKey,
    offset: triggerState.startOffset,
  };

  editor.update(() => {
    const node = $getNodeByKey(triggerState.nodeKey);
    if (!$isTextNode(node)) return;

    node.spliceText(
      triggerState.startOffset,
      triggerState.endOffset - triggerState.startOffset,
      '',
    );
    const nextOffset = Math.min(triggerState.startOffset, node.getTextContentSize());
    node.select(nextOffset, nextOffset);
  }, { discrete: true });

  return insertAnchor;
};

export const useInlineSuggestionController = ({
  menuState,
  setMenuState,
  options,
  editorRef,
  isComposing,
  insertInlineBox,
  createInlineBox,
  onOptionSelect,
  allowEmpty = false,
}: {
  menuState: MentionMenuState | null;
  setMenuState: Dispatch<SetStateAction<MentionMenuState | null>>;
  options: Array<AtImageOption | SkillOption>;
  editorRef: React.MutableRefObject<LexicalEditor | null>;
  isComposing: boolean;
  insertInlineBox: (inlineBox: IInlineBox, options?: MarkInsertOptions) => void;
  createInlineBox: (option: AtImageOption | SkillOption) => IInlineBox;
  onOptionSelect?: (option: AtImageOption | SkillOption) => void;
  allowEmpty?: boolean;
}) => {
  const createInlineBoxRef = useRef(createInlineBox);
  createInlineBoxRef.current = createInlineBox;
  const [activeIndex, setActiveIndex] = useState(0);

  const closeMenu = useCallback(() => {
    setMenuState(null);
  }, [setMenuState]);

  const filteredOptions = useMemo(() => {
    if (!menuState) return [];
    return filterSuggestionOptions(options, menuState.query);
  }, [menuState, options]);

  useEffect(() => {
    setActiveIndex(0);
  }, [menuState?.query]);

  useEffect(() => {
    if (activeIndex < filteredOptions.length) return;
    setActiveIndex(0);
  }, [activeIndex, filteredOptions.length]);

  useEffect(() => {
    if (!filteredOptions.length && menuState && !allowEmpty) {
      closeMenu();
    }
  }, [allowEmpty, closeMenu, filteredOptions.length, menuState]);

  const onOptionSelectRef = useLatestRef(onOptionSelect);

  const insertOption = useCallback((option: AtImageOption | SkillOption) => {
    if (!menuState) return;

    const editor = editorRef.current;
    if (!editor) return;

    const insertAnchor = replaceTriggerText({ editor, triggerState: menuState });

    const inlineBox = createInlineBoxRef.current(option);

    editor.getRootElement()?.focus();
    closeMenu();
    insertInlineBox(inlineBox, { insertAnchor });
    onOptionSelectRef.current?.(option);
  }, [closeMenu, editorRef, insertInlineBox, menuState, onOptionSelectRef]);

  const handleEnterCommand = useCallback(() => {
    if (isComposing) return false;
    if (!menuState || !filteredOptions.length) return false;

    insertOption(filteredOptions[activeIndex]);
    return true;
  }, [activeIndex, filteredOptions, insertOption, isComposing, menuState]);

  const handleSuggestionKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isComposing) return false;
    if (!menuState || !filteredOptions.length) return false;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => ((prev + 1) % filteredOptions.length));
      return true;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => ((prev - 1 + filteredOptions.length) % filteredOptions.length));
      return true;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      return true;
    }
    return false;
  }, [closeMenu, filteredOptions.length, isComposing, menuState]);

  return {
    activeIndex,
    closeMenu,
    filteredOptions,
    handleEnterCommand,
    handleSuggestionKeyDown,
    insertOption,
  };
};

export const useEditorChangeHandler = ({
  onChange,
  scrollCaretIntoView,
  markResultsMapRef,
}: {
  onChange?: (params: { value: EditorSerializedContent; }) => void;
  scrollCaretIntoView: () => void;
  markResultsMapRef: React.MutableRefObject<Map<string, MarkResult[]>>;
}) => {
  const lastEmittedSignatureRef = useRef<string | null>(null);
  const onChangeRef = useLatestRef(onChange);

  const handleOnChange = useCallback((editorState: EditorState) => {
    const content = serializeEditorStateToContent(editorState, markResultsMapRef.current);
    const signature = JSON.stringify(content);

    if (signature !== lastEmittedSignatureRef.current) {
      lastEmittedSignatureRef.current = signature;
      onChangeRef.current?.({ value: content });
      scrollCaretIntoView();
    }
  }, [onChangeRef, scrollCaretIntoView, markResultsMapRef]);

  /** 程序化替换内容后调用，同步签名以避免下次 onChange 发出过时通知 */
  const syncSignature = useCallback((content: EditorSerializedContent) => {
    lastEmittedSignatureRef.current = JSON.stringify(content);
  }, []);

  return {
    handleOnChange,
    syncSignature,
  };
};
